/**
 * PLC 仿真执行服务
 * 
 * 功能：解释执行 ST/IL 代码，支持基础逻辑运算
 * 
 * 架构设计：
 * ┌─────────────────────────────────────────────────────┐
 * │                  PlcSimulatorService                │
 * ├─────────────────────────────────────────────────────┤
 * │  ┌─────────────┐  ┌─────────────┐  ┌─────────────┐ │
 * │  │ ST 解释器    │  │ IL 解释器   │  │ 值比较器    │  │
 * │  └─────────────┘  └─────────────┘  └─────────────┘ │
 * │         │                │                │        │
 * │         └────────────────┴────────────────┘        │
 * │                         │                          │
 * │              ┌──────────┴──────────┐               │
 * │              │    变量上下文管理    │               │
 * │              └─────────────────────┘               │
 * └─────────────────────────────────────────────────────┘
 */

import type { PlcLanguageType } from '../types';
import type {
  PlcVariable,
  PlcDataType,
  SimulationContext,
  SimulationResult,
  TestCase,
  TestCaseResult,
  VariableTestResult
} from '../types/testTypes';
import { parseVariableValue, formatVariableValue } from './plcCodeService';

// ============================================
// 类型定义
// ============================================

/** 运行时变量上下文 */
interface RuntimeContext {
  variables: Record<string, unknown>;
  logs: string[];
}

/** 表达式求值结果 */
interface EvalResult {
  value: unknown;
  success: boolean;
  error?: string;
}

/** 块执行结果 - 用于控制流 */
interface BlockResult {
  /** 是否遇到了 EXIT 语句 */
  exitRequested: boolean;
}

// ============================================
// 数组下标解析工具
// ============================================

/**
 * 解析数组下标表达式，返回计算后的数字索引
 * 例如: "j + 1" 在 j=2 时返回 3
 */
const evaluateArrayIndex = (
  indexExpr: string,
  context: RuntimeContext
): number | null => {
  const result = evaluateSTExpression(indexExpr, context);
  if (result.success && typeof result.value === 'number') {
    return result.value;
  }
  // 尝试直接解析数字
  const num = Number(indexExpr.trim());
  return isNaN(num) ? null : num;
};

/**
 * 在表达式中展开所有数组下标引用
 * 将 tempArray[j+1] 替换为 context 中对应的值
 * 
 * 必须在变量替换之前调用，因为下标表达式中可能包含变量
 */
const expandArrayReferences = (
  expression: string,
  context: RuntimeContext
): string => {
  // 匹配数组引用: arrayName[indexExpr]
  // 需要支持嵌套，如 arr[i + 1]
  let result = expression;
  let changed = true;
  let iterations = 0;
  const MAX_EXPAND_ITERATIONS = 20;
  
  while (changed && iterations < MAX_EXPAND_ITERATIONS) {
    changed = false;
    iterations++;
    
    // 匹配最内层的数组引用（不含嵌套方括号）
    const arrayRefRegex = /(\w+)\[([^\[\]]+)\]/g;
    const newResult = result.replace(arrayRefRegex, (_match, arrayName: string, indexExpr: string) => {
      // 先对下标表达式求值
      const index = evaluateArrayIndex(indexExpr, context);
      if (index === null) {
        return _match; // 无法求值，保持原样
      }
      
      const key = `${arrayName}[${index}]`;
      const value = context.variables[key];
      
      if (value !== undefined) {
        changed = true;
        if (typeof value === 'boolean') {
          return value ? 'true' : 'false';
        } else if (typeof value === 'string') {
          return `"${value}"`;
        }
        return String(value);
      }
      
      // 变量不存在，返回默认值 0
      changed = true;
      return '0';
    });
    
    result = newResult;
  }
  
  return result;
};

// ============================================
// ST 语言解释器
// ============================================

/**
 * 安全的表达式求值器
 * 将 ST 表达式转换为可执行的 JavaScript
 */
const evaluateSTExpression = (
  expression: string,
  context: RuntimeContext
): EvalResult => {
  try {
    // 预处理表达式
    let jsExpr = expression.trim();

    // 先展开数组引用（在变量替换之前）
    jsExpr = expandArrayReferences(jsExpr, context);
    
    // 替换变量引用（先替换变量，再替换运算符，避免变量名中包含关键字被误替换）
    const varNames = Object.keys(context.variables)
      .filter(name => !name.includes('[')) // 排除数组元素，已在上面处理
      .sort((a, b) => b.length - a.length); // 长名称优先替换，避免子串问题
    for (const varName of varNames) {
      const regex = new RegExp(`\\b${varName}\\b`, 'g');
      const value = context.variables[varName];
      
      if (typeof value === 'boolean') {
        jsExpr = jsExpr.replace(regex, value ? 'true' : 'false');
      } else if (typeof value === 'string') {
        jsExpr = jsExpr.replace(regex, `"${value}"`);
      } else {
        jsExpr = jsExpr.replace(regex, String(value));
      }
    }
    
    // 替换 ST 运算符为 JavaScript 运算符
    jsExpr = jsExpr
      .replace(/\bAND\b/gi, '&&')
      .replace(/\bOR\b/gi, '||')
      .replace(/\bNOT\b/gi, '!')
      .replace(/\bXOR\b/gi, '^')
      .replace(/\bMOD\b/gi, '%')
      .replace(/\bTRUE\b/gi, 'true')
      .replace(/\bFALSE\b/gi, 'false')
      .replace(/:=/g, '=')
      .replace(/<>/g, '!==')
      .replace(/(?<![<>!])=(?!=)/g, '==='); // 只替换单独的 = 为 ===，不影响 <=, >=, <>, :=, !==
    
    // 安全执行（使用 Function 构造器而非 eval）
    const result = new Function(`return (${jsExpr})`)();
    
    return { value: result, success: true };
  } catch (error) {
    return {
      value: null,
      success: false,
      error: `表达式求值失败: ${expression} - ${error instanceof Error ? error.message : '未知错误'}`
    };
  }
};

/**
 * 执行 ST 赋值语句（支持数组元素赋值）
 */
const executeSTAssignment = (
  line: string,
  context: RuntimeContext
): boolean => {
  // 尝试匹配数组元素赋值: arrayName[indexExpr] := value;
  const arrayAssignMatch = line.match(/(\w+)\[(.+?)\]\s*:=\s*(.+?)\s*;?\s*$/);
  
  if (arrayAssignMatch) {
    const [, arrayName, indexExpr, expression] = arrayAssignMatch;
    
    // 求值下标
    const index = evaluateArrayIndex(indexExpr, context);
    if (index === null) {
      context.logs.push(`错误: 数组下标求值失败: ${indexExpr}`);
      return false;
    }
    
    const key = `${arrayName}[${index}]`;
    const result = evaluateSTExpression(expression, context);
    
    if (result.success) {
      context.variables[key] = result.value;
      context.logs.push(`${key} := ${result.value}`);
      return true;
    } else {
      context.logs.push(`错误: ${result.error}`);
      return false;
    }
  }

  // 普通变量赋值: varName := value;
  const assignMatch = line.match(/(\w+)\s*:=\s*(.+?)\s*;?\s*$/);
  
  if (!assignMatch) {
    return false;
  }
  
  const [, varName, expression] = assignMatch;
  const result = evaluateSTExpression(expression, context);
  
  if (result.success) {
    context.variables[varName] = result.value;
    context.logs.push(`${varName} := ${result.value}`);
    return true;
  } else {
    context.logs.push(`错误: ${result.error}`);
    return false;
  }
};

// ============================================
// 块匹配工具
// ============================================

/** 块的开始和结束关键词映射 */
const BLOCK_PAIRS: ReadonlyArray<{ start: RegExp; end: RegExp }> = [
  { start: /^IF\b/i, end: /^END_IF/i },
  { start: /^FOR\b/i, end: /^END_FOR/i },
  { start: /^CASE\b/i, end: /^END_CASE/i },
  { start: /^WHILE\b/i, end: /^END_WHILE/i },
  { start: /^REPEAT\b/i, end: /^END_REPEAT/i },
];

/**
 * 查找匹配的块结束位置
 * 支持嵌套块的正确匹配
 */
const findBlockEnd = (
  lines: readonly string[],
  startIndex: number,
  startPattern: RegExp,
  endPattern: RegExp
): number => {
  let depth = 0;
  for (let i = startIndex; i < lines.length; i++) {
    const line = lines[i].trim();
    if (startPattern.test(line)) {
      depth++;
    }
    if (endPattern.test(line)) {
      depth--;
      if (depth === 0) {
        return i;
      }
    }
  }
  return lines.length - 1; // 未找到匹配，返回末尾
};

// ============================================
// 递归块执行器
// ============================================

/**
 * 递归执行一段 ST 代码行
 * 返回是否遇到 EXIT 语句（用于跳出 FOR 循环）
 */
const executeSTBlock = (
  lines: readonly string[],
  start: number,
  end: number,
  context: RuntimeContext
): BlockResult => {
  let i = start;
  
  while (i <= end) {
    const line = lines[i].trim();
    
    // 跳过空行
    if (!line) {
      i++;
      continue;
    }
    
    // 跳过声明块关键字
    if (/^(PROGRAM|FUNCTION_BLOCK|FUNCTION|VAR_INPUT|VAR_OUTPUT|VAR_IN_OUT|VAR_TEMP|VAR|END_VAR|END_PROGRAM|END_FUNCTION_BLOCK|END_FUNCTION|BEGIN|VERSION\b)/i.test(line)) {
      i++;
      continue;
    }
    
    // 跳过花括号属性 { ... }
    if (/^\{.*\}$/.test(line)) {
      i++;
      continue;
    }
    
    // 跳过注释行 (* ... *) 或 //
    if (/^\(\*/.test(line) || /^\/\//.test(line)) {
      i++;
      continue;
    }
    
    // 跳过变量声明行（如 temp1, temp2, temp3 : REAL;）
    if (/^\w[\w\s,]*:\s*(BOOL|INT|DINT|REAL|LREAL|BYTE|WORD|DWORD|STRING)/i.test(line)) {
      i++;
      continue;
    }

    // 跳过数组声明行（如 tempArray : ARRAY[1..5] OF REAL;）
    if (/^\w[\w\s,]*:\s*ARRAY\s*\[/i.test(line)) {
      i++;
      continue;
    }

    // EXIT 语句
    if (/^EXIT\s*;?\s*$/i.test(line)) {
      return { exitRequested: true };
    }
    
    // ─────────── FOR 语句 ───────────
    if (/^FOR\b/i.test(line)) {
      const result = executeSTForBlock(lines, i, context);
      i = result.nextIndex;
      if (result.exitRequested) {
        // EXIT 在 FOR 内部已处理，FOR 本身被终止
        // 但不向上传播 EXIT（EXIT 只跳出最内层 FOR）
      }
      continue;
    }
    
    // ─────────── IF 语句 ───────────
    if (/^IF\b/i.test(line)) {
      const result = executeSTIfBlockRecursive(lines, i, context);
      i = result.nextIndex;
      if (result.exitRequested) {
        return { exitRequested: true };
      }
      continue;
    }
    
    // ─────────── CASE 语句 ───────────
    if (/^CASE\b/i.test(line)) {
      const result = executeSTCaseBlock(lines, i, context);
      i = result.nextIndex;
      if (result.exitRequested) {
        return { exitRequested: true };
      }
      continue;
    }
    
    // ─────────── 赋值语句 ───────────
    if (/:=/.test(line)) {
      executeSTAssignment(line, context);
    }
    
    i++;
  }
  
  return { exitRequested: false };
};

// ============================================
// FOR 循环执行器
// ============================================

interface ForBlockResult extends BlockResult {
  nextIndex: number;
}

/**
 * 执行 ST FOR 循环
 * 语法: FOR var := start TO end [BY step] DO ... END_FOR
 */
const executeSTForBlock = (
  lines: readonly string[],
  startIndex: number,
  context: RuntimeContext
): ForBlockResult => {
  const line = lines[startIndex].trim();
  
  // 解析 FOR 语句
  const forMatch = line.match(
    /^FOR\s+(\w+)\s*:=\s*(.+?)\s+TO\s+(.+?)(?:\s+BY\s+(.+?))?\s+DO\s*$/i
  );
  
  if (!forMatch) {
    context.logs.push(`错误: 无法解析 FOR 语句: ${line}`);
    return { nextIndex: startIndex + 1, exitRequested: false };
  }
  
  const [, varName, startExpr, endExpr, stepExpr] = forMatch;
  
  // 求值起止值
  const startVal = evaluateSTExpression(startExpr, context);
  const endVal = evaluateSTExpression(endExpr, context);
  const stepVal = stepExpr 
    ? evaluateSTExpression(stepExpr, context) 
    : { value: 1, success: true };
  
  if (!startVal.success || !endVal.success || !stepVal.success) {
    context.logs.push(`错误: FOR 循环参数求值失败`);
    return { nextIndex: startIndex + 1, exitRequested: false };
  }
  
  const start = Number(startVal.value);
  const end = Number(endVal.value);
  const step = Number(stepVal.value) || 1;
  
  // 找到 END_FOR
  const endForIndex = findBlockEnd(lines, startIndex, /^FOR\b/i, /^END_FOR/i);
  
  // 循环体范围
  const bodyStart = startIndex + 1;
  const bodyEnd = endForIndex - 1;
  
  // 防止无限循环
  const MAX_ITERATIONS = 10000;
  let iterations = 0;
  
  // 执行循环
  let exitRequested = false;
  
  if (step > 0) {
    for (let val = start; val <= end && iterations < MAX_ITERATIONS; val += step) {
      context.variables[varName] = val;
      iterations++;
      
      const blockResult = executeSTBlock(lines, bodyStart, bodyEnd, context);
      if (blockResult.exitRequested) {
        exitRequested = false; // EXIT 只终止当前 FOR，不向上传播
        break;
      }
    }
  } else if (step < 0) {
    for (let val = start; val >= end && iterations < MAX_ITERATIONS; val += step) {
      context.variables[varName] = val;
      iterations++;
      
      const blockResult = executeSTBlock(lines, bodyStart, bodyEnd, context);
      if (blockResult.exitRequested) {
        exitRequested = false;
        break;
      }
    }
  }
  
  return { nextIndex: endForIndex + 1, exitRequested };
};

// ============================================
// IF 语句执行器（递归版）
// ============================================

interface IfBlockResult extends BlockResult {
  nextIndex: number;
}

/**
 * 递归执行 IF/ELSIF/ELSE/END_IF 语句块
 */
const executeSTIfBlockRecursive = (
  lines: readonly string[],
  startIndex: number,
  context: RuntimeContext
): IfBlockResult => {
  const endIfIndex = findBlockEnd(lines, startIndex, /^IF\b/i, /^END_IF/i);
  
  // 收集各分支: { condition, bodyStart, bodyEnd }
  interface Branch {
    condition: string | null; // null 表示 ELSE 分支
    bodyStart: number;
    bodyEnd: number;
  }
  
  const branches: Branch[] = [];
  let depth = 0;
  let currentCondition: string | null = null;
  let currentBodyStart = -1;
  
  for (let i = startIndex; i <= endIfIndex; i++) {
    const line = lines[i].trim();
    
    // 计算嵌套深度（排除自身所在层级）
    if (/^IF\b/i.test(line) && i !== startIndex) {
      depth++;
    }
    if (/^END_IF/i.test(line) && depth > 0) {
      depth--;
      continue;
    }
    
    if (depth > 0) {
      continue;
    }
    
    // 顶层 IF
    if (/^IF\b/i.test(line) && i === startIndex) {
      const condMatch = line.match(/^IF\s+(.+?)\s+THEN/i);
      currentCondition = condMatch ? condMatch[1] : 'TRUE';
      currentBodyStart = i + 1;
      continue;
    }
    
    // ELSIF
    if (/^ELSIF\b/i.test(line)) {
      // 关闭上一个分支
      if (currentBodyStart >= 0) {
        branches.push({
          condition: currentCondition,
          bodyStart: currentBodyStart,
          bodyEnd: i - 1
        });
      }
      const condMatch = line.match(/^ELSIF\s+(.+?)\s+THEN/i);
      currentCondition = condMatch ? condMatch[1] : 'TRUE';
      currentBodyStart = i + 1;
      continue;
    }
    
    // ELSE
    if (/^ELSE\s*$/i.test(line)) {
      if (currentBodyStart >= 0) {
        branches.push({
          condition: currentCondition,
          bodyStart: currentBodyStart,
          bodyEnd: i - 1
        });
      }
      currentCondition = null; // ELSE 无条件
      currentBodyStart = i + 1;
      continue;
    }
    
    // END_IF
    if (/^END_IF/i.test(line)) {
      if (currentBodyStart >= 0) {
        branches.push({
          condition: currentCondition,
          bodyStart: currentBodyStart,
          bodyEnd: i - 1
        });
      }
    }
  }
  
  // 执行第一个条件为真的分支
  for (const branch of branches) {
    if (branch.condition === null) {
      // ELSE 分支 - 前面没有匹配的就执行这个
      const blockResult = executeSTBlock(lines, branch.bodyStart, branch.bodyEnd, context);
      return { nextIndex: endIfIndex + 1, exitRequested: blockResult.exitRequested };
    }
    
    const result = evaluateSTExpression(branch.condition, context);
    if (result.success && Boolean(result.value)) {
      const blockResult = executeSTBlock(lines, branch.bodyStart, branch.bodyEnd, context);
      return { nextIndex: endIfIndex + 1, exitRequested: blockResult.exitRequested };
    }
  }
  
  return { nextIndex: endIfIndex + 1, exitRequested: false };
};

// ============================================
// CASE 语句执行器
// ============================================

interface CaseBlockResult extends BlockResult {
  nextIndex: number;
}

/**
 * 执行 CASE 语句
 * 语法: CASE expr OF value1: ... value2: ... [ELSE ...] END_CASE
 */
const executeSTCaseBlock = (
  lines: readonly string[],
  startIndex: number,
  context: RuntimeContext
): CaseBlockResult => {
  const line = lines[startIndex].trim();
  const endCaseIndex = findBlockEnd(lines, startIndex, /^CASE\b/i, /^END_CASE/i);
  
  // 解析 CASE 表达式
  const caseMatch = line.match(/^CASE\s+(.+?)\s+OF\s*$/i);
  if (!caseMatch) {
    context.logs.push(`错误: 无法解析 CASE 语句: ${line}`);
    return { nextIndex: endCaseIndex + 1, exitRequested: false };
  }
  
  const caseExpr = evaluateSTExpression(caseMatch[1], context);
  if (!caseExpr.success) {
    context.logs.push(`错误: CASE 表达式求值失败: ${caseMatch[1]}`);
    return { nextIndex: endCaseIndex + 1, exitRequested: false };
  }
  
  const caseValue = caseExpr.value;
  
  // 收集各分支
  interface CaseBranch {
    values: unknown[] | null; // null 表示 ELSE
    bodyStart: number;
    bodyEnd: number;
  }
  
  const branches: CaseBranch[] = [];
  let depth = 0;
  let currentValues: unknown[] | null = null;
  let currentBodyStart = -1;
  
  for (let i = startIndex + 1; i <= endCaseIndex; i++) {
    const branchLine = lines[i].trim();
    
    // 跟踪嵌套块深度
    for (const pair of BLOCK_PAIRS) {
      if (pair.start.test(branchLine)) depth++;
      if (pair.end.test(branchLine)) depth--;
    }
    
    if (depth > 0) continue;
    
    // ELSE 分支
    if (/^ELSE\s*:?\s*$/i.test(branchLine)) {
      if (currentBodyStart >= 0) {
        branches.push({ values: currentValues, bodyStart: currentBodyStart, bodyEnd: i - 1 });
      }
      currentValues = null;
      currentBodyStart = i + 1;
      continue;
    }
    
    // END_CASE
    if (/^END_CASE/i.test(branchLine)) {
      if (currentBodyStart >= 0) {
        branches.push({ values: currentValues, bodyStart: currentBodyStart, bodyEnd: i - 1 });
      }
      continue;
    }
    
    // 值标签行（如 1: 或 1, 2, 3: 或 1..5:）
    const labelMatch = branchLine.match(/^([\d\w\s,.\-]+)\s*:\s*$/);
    if (labelMatch) {
      if (currentBodyStart >= 0) {
        branches.push({ values: currentValues, bodyStart: currentBodyStart, bodyEnd: i - 1 });
      }
      
      // 解析值列表
      const valueStr = labelMatch[1].trim();
      const parsedValues: unknown[] = [];
      
      // 支持逗号分隔和范围
      const parts = valueStr.split(',').map(s => s.trim());
      for (const part of parts) {
        const rangeMatch = part.match(/^(\d+)\s*\.\.\s*(\d+)$/);
        if (rangeMatch) {
          const rangeStart = Number(rangeMatch[1]);
          const rangeEnd = Number(rangeMatch[2]);
          for (let v = rangeStart; v <= rangeEnd; v++) {
            parsedValues.push(v);
          }
        } else {
          // 尝试数字
          const num = Number(part);
          parsedValues.push(isNaN(num) ? part : num);
        }
      }
      
      currentValues = parsedValues;
      currentBodyStart = i + 1;
      continue;
    }
  }
  
  // 匹配并执行
  for (const branch of branches) {
    if (branch.values === null) {
      // ELSE 分支
      const blockResult = executeSTBlock(lines, branch.bodyStart, branch.bodyEnd, context);
      return { nextIndex: endCaseIndex + 1, exitRequested: blockResult.exitRequested };
    }
    
    // 值匹配（支持数字和字符串比较）
    const matched = branch.values.some(v => {
      if (typeof v === 'number' && typeof caseValue === 'number') {
        return v === caseValue;
      }
      return String(v) === String(caseValue);
    });
    
    if (matched) {
      const blockResult = executeSTBlock(lines, branch.bodyStart, branch.bodyEnd, context);
      return { nextIndex: endCaseIndex + 1, exitRequested: blockResult.exitRequested };
    }
  }
  
  return { nextIndex: endCaseIndex + 1, exitRequested: false };
};

// ============================================
// S7 语法预处理器
// ============================================

/**
 * 将 S7/TIA Portal 风格的 ST 代码预处理为标准 ST 语法
 * 
 * 处理内容：
 * 1. 去除变量引用的 # 前缀（#a1 → a1）
 * 2. 去除 FUNCTION_BLOCK/FUNCTION 名称的引号
 * 3. 去除 TITLE 行等 S7 特有元数据
 */
const preprocessS7Code = (code: string): string => {
  let result = code;
  
  // 去除 FUNCTION_BLOCK/FUNCTION/PROGRAM 名称中的引号
  // 例如: FUNCTION_BLOCK "FB_BubbleSort" → FUNCTION_BLOCK FB_BubbleSort
  result = result.replace(
    /^(\s*(?:FUNCTION_BLOCK|FUNCTION|PROGRAM))\s+"([^"]+)"/gim,
    '$1 $2'
  );
  
  // 去除变量引用的 # 前缀
  // #varName → varName（但不影响字符串常量中的 #）
  // 匹配 # 后跟字母或下划线开头的标识符
  result = result.replace(/#(\w+)/g, '$1');
  
  return result;
};

// ============================================
// ST 代码入口执行器
// ============================================

/**
 * ST 代码解释执行器
 */
const executeSTCode = (
  code: string,
  initialVariables: Record<string, unknown>
): SimulationResult => {
  const context: RuntimeContext = {
    variables: { ...initialVariables },
    logs: []
  };
  
  try {
    // 移除块注释
    const cleanCode = code
      .replace(/\(\*[\s\S]*?\*\)/g, '')
      .replace(/\/\/.*$/gm, '');

    // S7 语法预处理：去除 # 前缀、引号等
    const normalizedCode = preprocessS7Code(cleanCode);
    
    const lines = normalizedCode.split('\n');
    const trimmedLines = lines.map(l => l.trim());
    
    // 使用递归块执行器
    executeSTBlock(trimmedLines, 0, trimmedLines.length - 1, context);
    
    return {
      success: true,
      outputValues: context.variables,
      logs: context.logs
    };
  } catch (error) {
    return {
      success: false,
      outputValues: context.variables,
      error: error instanceof Error ? error.message : '执行错误',
      logs: context.logs
    };
  }
};

// ============================================
// IL 语言解释器
// ============================================

/**
 * IL 代码解释执行器
 */
const executeILCode = (
  code: string,
  initialVariables: Record<string, unknown>
): SimulationResult => {
  const context: RuntimeContext = {
    variables: { ...initialVariables },
    logs: []
  };
  
  try {
    // 移除注释
    const cleanCode = code.replace(/\(\*[\s\S]*?\*\)/g, '');
    const lines = cleanCode.split('\n').map(l => l.trim()).filter(l => l);
    
    // 累加器（IL 使用累加器模型）
    let accumulator: unknown = false;
    
    for (const line of lines) {
      // 跳过标签
      if (line.endsWith(':')) {
        continue;
      }
      
      // 解析指令
      const parts = line.split(/\s+/);
      const instruction = parts[0]?.toUpperCase();
      const operand = parts[1];
      
      switch (instruction) {
        case 'LD': // 加载
          accumulator = operand ? (context.variables[operand] ?? operand) : accumulator;
          break;
        case 'LDN': // 加载取反
          accumulator = !(context.variables[operand] ?? false);
          break;
        case 'ST': // 存储
          if (operand) {
            context.variables[operand] = accumulator;
            context.logs.push(`${operand} := ${accumulator}`);
          }
          break;
        case 'STN': // 存储取反
          if (operand) {
            context.variables[operand] = !accumulator;
            context.logs.push(`${operand} := ${!accumulator}`);
          }
          break;
        case 'AND': // 与
          accumulator = Boolean(accumulator) && Boolean(context.variables[operand] ?? false);
          break;
        case 'ANDN': // 与非
          accumulator = Boolean(accumulator) && !Boolean(context.variables[operand] ?? false);
          break;
        case 'OR': // 或
          accumulator = Boolean(accumulator) || Boolean(context.variables[operand] ?? false);
          break;
        case 'ORN': // 或非
          accumulator = Boolean(accumulator) || !Boolean(context.variables[operand] ?? false);
          break;
        case 'NOT': // 非
          accumulator = !Boolean(accumulator);
          break;
        case 'S': // 置位
          if (operand && accumulator) {
            context.variables[operand] = true;
          }
          break;
        case 'R': // 复位
          if (operand && accumulator) {
            context.variables[operand] = false;
          }
          break;
        case 'ADD': // 加法
          accumulator = Number(accumulator) + Number(context.variables[operand] ?? 0);
          break;
        case 'SUB': // 减法
          accumulator = Number(accumulator) - Number(context.variables[operand] ?? 0);
          break;
        case 'MUL': // 乘法
          accumulator = Number(accumulator) * Number(context.variables[operand] ?? 1);
          break;
        case 'DIV': // 除法
          const divisor = Number(context.variables[operand] ?? 1);
          accumulator = divisor !== 0 ? Number(accumulator) / divisor : 0;
          break;
        case 'GT': // 大于
          accumulator = Number(accumulator) > Number(context.variables[operand] ?? 0);
          break;
        case 'GE': // 大于等于
          accumulator = Number(accumulator) >= Number(context.variables[operand] ?? 0);
          break;
        case 'EQ': // 等于
          accumulator = accumulator === (context.variables[operand] ?? operand);
          break;
        case 'NE': // 不等于
          accumulator = accumulator !== (context.variables[operand] ?? operand);
          break;
        case 'LT': // 小于
          accumulator = Number(accumulator) < Number(context.variables[operand] ?? 0);
          break;
        case 'LE': // 小于等于
          accumulator = Number(accumulator) <= Number(context.variables[operand] ?? 0);
          break;
        case 'RET': // 返回
        case 'RETC':
        case 'RETCN':
          break;
        default:
          // 未知指令，跳过
          break;
      }
    }
    
    return {
      success: true,
      outputValues: context.variables,
      logs: context.logs
    };
  } catch (error) {
    return {
      success: false,
      outputValues: context.variables,
      error: error instanceof Error ? error.message : '执行错误',
      logs: context.logs
    };
  }
};

// ============================================
// 值比较器
// ============================================

/**
 * 比较两个值是否相等（支持类型转换和容差比较）
 */
const compareValues = (
  expected: unknown,
  actual: unknown,
  dataType: PlcDataType
): boolean => {
  // 布尔值比较
  if (dataType === 'BOOL') {
    const expectedBool = typeof expected === 'boolean' 
      ? expected 
      : /^(true|1)$/i.test(String(expected));
    const actualBool = typeof actual === 'boolean'
      ? actual
      : /^(true|1)$/i.test(String(actual));
    return expectedBool === actualBool;
  }
  
  // 浮点数比较（允许误差）
  if (dataType === 'REAL' || dataType === 'LREAL') {
    const expectedNum = Number(expected);
    const actualNum = Number(actual);
    const tolerance = 0.0001;
    return Math.abs(expectedNum - actualNum) < tolerance;
  }
  
  // 整数比较
  if (['INT', 'DINT', 'BYTE', 'WORD', 'DWORD'].includes(dataType)) {
    return Number(expected) === Number(actual);
  }
  
  // 字符串比较
  return String(expected) === String(actual);
};

// ============================================
// 导出的仿真服务
// ============================================

export const PlcSimulatorService = {
  /**
   * 执行仿真
   */
  execute(
    code: string,
    language: PlcLanguageType,
    inputValues: Record<string, unknown>
  ): SimulationResult {
    switch (language) {
      case 'st':
        return executeSTCode(code, inputValues);
      case 'il':
        return executeILCode(code, inputValues);
      case 'lad':
      case 'fbd':
        return {
          success: false,
          outputValues: {},
          error: '图形化语言暂不支持仿真执行'
        };
      default:
        return {
          success: false,
          outputValues: {},
          error: `不支持的语言类型: ${language}`
        };
    }
  },
  
  /**
   * 运行单个测试用例
   */
  runTestCase(
    testCase: TestCase,
    code: string,
    language: PlcLanguageType,
    variableDefinitions: readonly PlcVariable[]
  ): TestCaseResult {
    const startTime = performance.now();
    
    // 构建输入变量映射
    const inputValues: Record<string, unknown> = {};
    
    for (const input of testCase.inputs) {
      const varDef = variableDefinitions.find(v => v.name === input.variableName);
      if (varDef) {
        inputValues[input.variableName] = parseVariableValue(input.value, varDef.dataType);
      }
    }
    
    // 初始化所有变量
    for (const varDef of variableDefinitions) {
      if (!(varDef.name in inputValues)) {
        inputValues[varDef.name] = parseVariableValue(
          varDef.initialValue ?? '',
          varDef.dataType
        );
      }
    }
    
    // 执行仿真
    const result = this.execute(code, language, inputValues);
    const endTime = performance.now();
    
    if (!result.success) {
      return {
        testCaseId: testCase.id,
        passed: false,
        variableResults: [],
        executionTime: endTime - startTime,
        errorMessage: result.error,
        timestamp: new Date().toISOString()
      };
    }
    
    // 比较输出结果
    const variableResults: VariableTestResult[] = [];
    let allPassed = true;
    
    for (const expected of testCase.expectedOutputs) {
      const varDef = variableDefinitions.find(v => v.name === expected.variableName);
      const actualValue = result.outputValues[expected.variableName];
      const expectedParsed = varDef 
        ? parseVariableValue(expected.value, varDef.dataType)
        : expected.value;
      
      const passed = varDef
        ? compareValues(expectedParsed, actualValue, varDef.dataType)
        : String(expectedParsed) === String(actualValue);
      
      if (!passed) {
        allPassed = false;
      }
      
      variableResults.push({
        variableName: expected.variableName,
        expectedValue: expected.value,
        actualValue: varDef 
          ? formatVariableValue(actualValue, varDef.dataType)
          : String(actualValue ?? ''),
        passed
      });
    }
    
    return {
      testCaseId: testCase.id,
      passed: allPassed,
      variableResults,
      executionTime: endTime - startTime,
      timestamp: new Date().toISOString()
    };
  },
  
  /**
   * 批量运行测试用例
   */
  runAllTestCases(
    testCases: readonly TestCase[],
    code: string,
    language: PlcLanguageType,
    variableDefinitions: readonly PlcVariable[]
  ): Map<string, TestCaseResult> {
    const results = new Map<string, TestCaseResult>();
    
    for (const testCase of testCases) {
      const result = this.runTestCase(testCase, code, language, variableDefinitions);
      results.set(testCase.id, result);
    }
    
    return results;
  }
};

export default PlcSimulatorService;